/**
 * The sales ledger.
 *
 * ═══════════════════════════════════════════════════════════════════
 * THE SALES LEDGER IS NOT AN AFTERTHOUGHT.
 *
 * It has the same request-and-approve route as purchasing, the same posting
 * engine, the same drill-down to the journal, and the same reporting. A
 * department raises a request, finance approves it and converts it to an
 * invoice, and the invoice posts to the debtors control account.
 *
 * Organisations that recharge each other, let their premises, or bill a local
 * authority live in this screen as much as in the purchase ledger.
 * ═══════════════════════════════════════════════════════════════════
 *
 * Two quite different people share this screen, and they hold disjoint sets of
 * permissions. A department user holds `ar.request` and may raise a request and
 * see their own; they may not see the ledger at all. Finance holds `ar.view`
 * and `ar.invoice.create` and may see everything but does not raise requests.
 * Every tab, every column and every fetch below is chosen from what the signed-in
 * person actually holds, because asking the server for something they are not
 * allowed to have puts a permission error where their own work should be.
 */

import { useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../lib/api';
import { useAsync, useMutation } from '../lib/useAsync';
import { useSession } from '../lib/session';
import { useTerms, lower } from '../lib/tenant';
import { date, money, statusLabel, statusTone } from '../lib/format';
import {
  Alert,
  AsyncPanel,
  Badge,
  Card,
  DataTable,
  ErrorNotice,
  Modal,
  Money,
  PageHeader,
  Select,
  Tabs,
  TextArea,
  TextInput,
  type Column,
} from '../components/ui';

interface SalesInvoiceRow {
  id: string;
  invoice_no: string;
  customer_name: string;
  customer_code: string;
  invoice_date: string;
  due_date: string;
  gross_total_minor: string;
  outstanding_minor: string;
  status: string;
  comments: string | null;
  journal_no: string | null;
  journal_id: string | null;
  days_overdue: number | null;
}

/**
 * A sales invoice request as the server sends it — the columns of
 * `sales_invoice_request` plus the joined names. The amount and the raised date
 * are `requested_amount_minor` and `requested_at`; there is no `net_amount_minor`
 * and no `created_at` on this table.
 */
interface RequestRow {
  id: string;
  reference: string;
  customer_name: string | null;
  proposed_customer_name: string | null;
  description: string;
  requested_amount_minor: string;
  status: string;
  requested_by_name: string | null;
  account_code: string | null;
  cost_centre_code: string | null;
  approved_by_name: string | null;
  invoice_no: string | null;
  requested_at: string;
}

interface ConvertResult {
  invoiceId: string;
  invoiceNo: string;
  grossMinor: number;
  customerName: string | null;
}

interface PostResult {
  invoiceNo: string;
  journalNo: string;
  journalId: string;
}

export function SalesPage() {
  const t = useTerms();
  const { can } = useSession();
  const canViewLedger = can('ar.view');
  const canRaiseInvoice = can('ar.invoice.create');

  const [tab, setTab] = useState(canViewLedger ? 'invoices' : 'requests');
  const [raising, setRaising] = useState(false);
  // What just happened, in the presenter's own words. Converting a request or
  // posting an invoice moves a document to a different tab; without a line
  // saying so, the row simply disappears and nobody learns the outcome.
  const [outcome, setOutcome] = useState<string | null>(null);

  const invoices = useAsync(
    () =>
      canViewLedger
        ? api.get<{ invoices: SalesInvoiceRow[] }>('/receivables/invoices')
        : Promise.resolve({ invoices: [] as SalesInvoiceRow[] }),
    [canViewLedger],
  );
  const requests = useAsync(() => api.get<{ requests: RequestRow[] }>('/receivables/invoice-requests'), []);

  const convert = useMutation(async (requestId: string) =>
    api.post<ConvertResult>(`/receivables/invoice-requests/${requestId}/convert`, {}),
  );
  const post = useMutation(async (invoiceId: string) => api.post<PostResult>(`/receivables/invoices/${invoiceId}/post`, {}));

  // The two states that need finance to act: approve it, then convert it. These
  // are the values the column actually holds — 'draft', 'pending_approval',
  // 'approved', 'rejected', 'invoiced', 'cancelled'.
  const awaitingFinance = requests.data?.requests.filter(
    (r) => r.status === 'pending_approval' || r.status === 'approved',
  ).length;

  const tabs: Array<{ key: string; label: string; badge?: number }> = [
    ...(canViewLedger ? [{ key: 'invoices', label: 'Invoices' }] : []),
    { key: 'requests', label: 'Requests', badge: awaitingFinance },
  ];

  return (
    <>
      <PageHeader
        title="Sales invoices"
        subtitle={`Requests from ${lower(t.departmentPlural)}, approved by ${lower(t.financeTeam)}, converted into invoices that post to the ledger.`}
        actions={
          can('ar.request') ? (
            <button type="button" className="btn btn--primary" onClick={() => setRaising(true)}>
              Request an invoice
            </button>
          ) : null
        }
      />

      {/* A single tab is not a choice, so it is not offered as one. */}
      {tabs.length > 1 ? <Tabs active={tab} onChange={setTab} tabs={tabs} /> : null}

      {outcome ? (
        <Alert tone="ok" title="Done">
          {outcome}
        </Alert>
      ) : null}

      {tab === 'invoices' && canViewLedger ? (
        <AsyncPanel state={invoices}>
          {(data) => {
            const overdue = data.invoices.filter((i) => Number(i.days_overdue) > 0 && Number(i.outstanding_minor) > 0);
            const overdueMinor = overdue.reduce((sum, i) => sum + Number(i.outstanding_minor), 0);
            const grossMinor = data.invoices.reduce((sum, i) => sum + Number(i.gross_total_minor), 0);
            const outstandingMinor = data.invoices.reduce((sum, i) => sum + Number(i.outstanding_minor), 0);

            const columns: Array<Column<SalesInvoiceRow>> = [
              {
                key: 'no',
                header: 'Invoice',
                width: '10.5rem',
                // A document reference broken across two lines reads as a
                // layout nobody looked at, so it is never allowed to wrap.
                render: (i) => <span className="mono nowrap">{i.invoice_no}</span>,
                sortValue: (i) => i.invoice_no,
              },
              {
                key: 'customer',
                header: t.customer,
                render: (i) => (
                  <div style={{ maxWidth: '13rem' }}>
                    <div className="truncate" title={i.customer_name}>
                      {i.customer_name}
                    </div>
                    <div className="smaller muted mono">{i.customer_code}</div>
                  </div>
                ),
                sortValue: (i) => i.customer_name,
              },
              {
                key: 'desc',
                header: 'For',
                // Capped, or a long narrative takes the width the money columns
                // need and pushes the whole table into a horizontal scroll.
                render: (i) => (
                  <span className="truncate smaller" style={{ display: 'block', maxWidth: '11rem' }} title={i.comments ?? undefined}>
                    {i.comments ?? '—'}
                  </span>
                ),
              },
              {
                key: 'date',
                header: 'Dated',
                render: (i) => date(i.invoice_date),
                sortValue: (i) => i.invoice_date,
              },
              {
                key: 'due',
                header: 'Due',
                render: (i) => (
                  <span className={Number(i.days_overdue) > 0 && Number(i.outstanding_minor) > 0 ? 'num--negative' : undefined}>
                    {date(i.due_date)}
                  </span>
                ),
                sortValue: (i) => i.due_date,
              },
              {
                key: 'gross',
                header: 'Value',
                numeric: true,
                render: (i) => <Money minor={Number(i.gross_total_minor)} />,
                sortValue: (i) => Number(i.gross_total_minor),
              },
              {
                key: 'outstanding',
                header: 'Outstanding',
                numeric: true,
                render: (i) => <Money minor={Number(i.outstanding_minor)} blankIfZero />,
                sortValue: (i) => Number(i.outstanding_minor),
              },
              {
                key: 'journal',
                header: 'Journal',
                render: (i) =>
                  i.journal_id ? (
                    <Link to={`/ledger/journals/${i.journal_id}`}>
                      <span className="mono smaller">{i.journal_no}</span>
                    </Link>
                  ) : (
                    <span className="muted smaller">—</span>
                  ),
              },
              {
                key: 'status',
                header: 'Status',
                render: (i) => <Badge tone={statusTone(i.status)}>{statusLabel(i.status)}</Badge>,
                sortValue: (i) => statusLabel(i.status),
              },
            ];

            // A converted request arrives as a draft. Finishing the journey —
            // posting it to the debtors control account — belongs on the row
            // itself, and only for the people allowed to do it.
            if (canRaiseInvoice) {
              columns.push({
                key: 'action',
                header: <span className="visually-hidden">Post to the ledger</span>,
                render: (i) =>
                  i.status === 'draft' ? (
                    <button
                      type="button"
                      className="btn btn--sm btn--primary"
                      disabled={post.busy}
                      onClick={() =>
                        void post.run(i.id).then((result) => {
                          if (!result) return;
                          setOutcome(`${result.invoiceNo} posted to the ledger as journal ${result.journalNo}.`);
                          invoices.reload();
                        })
                      }
                    >
                      Post to the ledger
                    </button>
                  ) : null,
              });
            }

            return (
              <Card
                title="Sales ledger"
                subtitle={
                  overdue.length
                    ? `${overdue.length} ${overdue.length === 1 ? 'invoice is' : 'invoices are'} overdue, ${money(overdueMinor)}.`
                    : 'Nothing is overdue.'
                }
                flush
              >
                <ErrorNotice error={post.error} />
                <DataTable
                  caption="Sales invoices"
                  rows={data.invoices}
                  rowKey={(i) => i.id}
                  emptyMessage="No sales invoices have been raised yet."
                  pageSize={25}
                  columns={columns}
                  footer={
                    <tr className="is-total">
                      {/* Every invoice in the ledger, not just the page on screen. */}
                      <td colSpan={5}>Total — {data.invoices.length === 1 ? '1 invoice' : `${data.invoices.length} invoices`}</td>
                      <td className="num">{money(grossMinor)}</td>
                      <td className="num">{money(outstandingMinor)}</td>
                      <td colSpan={canRaiseInvoice ? 3 : 2} />
                    </tr>
                  }
                />
              </Card>
            );
          }}
        </AsyncPanel>
      ) : (
        <Card
          title="Invoice requests"
          subtitle={`Raised by a ${lower(t.department)}, approved and converted by ${lower(t.financeTeam)}.`}
          flush
        >
          <AsyncPanel state={requests}>
            {(data) => (
              <>
                <ErrorNotice error={convert.error} />
                <DataTable
                  caption="Sales invoice requests"
                  rows={data.requests}
                  rowKey={(r) => r.id}
                  emptyMessage={
                    canViewLedger
                      ? `No ${lower(t.department)} has raised an invoice request.`
                      : 'You have not raised an invoice request yet.'
                  }
                  pageSize={25}
                  columns={[
                    {
                      key: 'ref',
                      header: 'Request',
                      width: '10rem',
                      render: (r) => <span className="mono smaller nowrap">{r.reference}</span>,
                      sortValue: (r) => r.reference,
                    },
                    {
                      key: 'raised',
                      header: 'Raised',
                      render: (r) => <span className="smaller">{date(r.requested_at)}</span>,
                      sortValue: (r) => r.requested_at,
                    },
                    {
                      key: 'customer',
                      header: t.customer,
                      // A request may name someone who is not on the ledger yet;
                      // finance creates the record when it converts the request.
                      render: (r) =>
                        r.customer_name ? (
                          <span className="truncate" style={{ display: 'block', maxWidth: '13rem' }} title={r.customer_name}>
                            {r.customer_name}
                          </span>
                        ) : r.proposed_customer_name ? (
                          <div style={{ maxWidth: '13rem' }}>
                            <div className="truncate" title={r.proposed_customer_name}>
                              {r.proposed_customer_name}
                            </div>
                            <div className="smaller muted nowrap">New {lower(t.customer)}</div>
                          </div>
                        ) : (
                          <span className="muted">—</span>
                        ),
                      sortValue: (r) => r.customer_name ?? r.proposed_customer_name ?? '',
                    },
                    {
                      key: 'desc',
                      header: 'For',
                      render: (r) => (
                        <span className="truncate" style={{ display: 'block', maxWidth: '12rem' }} title={r.description}>
                          {r.description}
                        </span>
                      ),
                    },
                    {
                      key: 'coding',
                      header: 'Coding',
                      render: (r) => (
                        <span className="mono smaller nowrap">
                          {r.account_code ?? '—'}
                          {r.cost_centre_code ? ` / ${r.cost_centre_code}` : ''}
                        </span>
                      ),
                    },
                    {
                      key: 'by',
                      header: 'Requested by',
                      render: (r) => <span className="smaller">{r.requested_by_name ?? '—'}</span>,
                      sortValue: (r) => r.requested_by_name ?? '',
                    },
                    {
                      key: 'net',
                      header: 'Net',
                      numeric: true,
                      render: (r) => <Money minor={Number(r.requested_amount_minor)} />,
                      sortValue: (r) => Number(r.requested_amount_minor),
                    },
                    {
                      key: 'status',
                      header: 'Status',
                      render: (r) => <Badge tone={statusTone(r.status)}>{statusLabel(r.status)}</Badge>,
                      sortValue: (r) => statusLabel(r.status),
                    },
                    {
                      key: 'action',
                      header: <span className="visually-hidden">Convert</span>,
                      render: (r) =>
                        r.status === 'approved' && !r.invoice_no && canRaiseInvoice ? (
                          <button
                            type="button"
                            className="btn btn--sm btn--primary"
                            disabled={convert.busy}
                            onClick={() =>
                              void convert.run(r.id).then((result) => {
                                if (!result) return;
                                setOutcome(
                                  `${result.invoiceNo} raised for ${result.customerName ?? r.customer_name ?? r.proposed_customer_name ?? 'the ' + lower(t.customer)}, ${money(result.grossMinor)}. It is a draft until it is posted to the ledger.`,
                                );
                                requests.reload();
                                invoices.reload();
                                if (canViewLedger) setTab('invoices');
                              })
                            }
                          >
                            Convert to invoice
                          </button>
                        ) : r.invoice_no ? (
                          <span className="mono smaller">{r.invoice_no}</span>
                        ) : null,
                    },
                  ]}
                />
              </>
            )}
          </AsyncPanel>
        </Card>
      )}

      <RequestInvoiceModal
        open={raising}
        onClose={() => setRaising(false)}
        onCreated={(message) => {
          setRaising(false);
          setOutcome(message);
          requests.reload();
          setTab('requests');
        }}
      />
    </>
  );
}

interface CustomerOption {
  id: string;
  code: string;
  name: string;
}

interface RequestRefs {
  customers: CustomerOption[];
  /**
   * True when the list was refused rather than empty. The customer list needs
   * `ar.view`, which the department users who raise these requests do not hold,
   * so that one call is allowed to fail on its own and the form falls back to
   * naming the customer in free text — a route the server supports directly.
   */
  customersRefused: boolean;
  accounts: Array<{ id: string; code: string; name: string; account_type: string }>;
  costCentres: Array<{ id: string; code: string; name: string; level_type: string }>;
  vatCodes: Array<{ code: string; name: string }>;
}

/** The sentinel value for "the customer is not on the list yet". */
const NEW_CUSTOMER = 'new';

function RequestInvoiceModal({
  open,
  onClose,
  onCreated,
}: {
  open: boolean;
  onClose: () => void;
  onCreated: (message: string) => void;
}) {
  const t = useTerms();
  const [customerId, setCustomerId] = useState('');
  const [newCustomerName, setNewCustomerName] = useState('');
  const [newCustomerEmail, setNewCustomerEmail] = useState('');
  const [description, setDescription] = useState('');
  const [amount, setAmount] = useState('');
  const [accountId, setAccountId] = useState('');
  const [costCentreId, setCostCentreId] = useState('');
  const [vatCode, setVatCode] = useState('OUT');

  const refs = useAsync<RequestRefs>(
    () =>
      open
        ? Promise.all([
            api
              .get<{ customers: CustomerOption[] }>('/reference/customers')
              .then((r) => r.customers)
              .catch(() => null),
            api.get<{ accounts: Array<{ id: string; code: string; name: string; account_type: string }> }>('/reference/accounts'),
            api.get<{ costCentres: Array<{ id: string; code: string; name: string; level_type: string }> }>(
              '/reference/cost-centres',
            ),
            api.get<{ vatCodes: Array<{ code: string; name: string }> }>('/reference/vat-codes'),
          ]).then(([customers, a, cc, v]) => ({
            customers: customers ?? [],
            customersRefused: customers === null,
            // The server refuses anything that is not an income code, so the
            // picker never offers one.
            accounts: a.accounts.filter((x) => x.account_type === 'income'),
            costCentres: cc.costCentres.filter((c) => c.level_type === 'cost_centre' || c.level_type === 'department'),
            vatCodes: v.vatCodes,
          }))
        : Promise.resolve({ customers: [], customersRefused: false, accounts: [], costCentres: [], vatCodes: [] }),
    [open],
  );

  // useAsync keeps the previous value while it reloads, and the previous value
  // here is the empty placeholder from when the dialog was shut. Blanking it
  // while the request is in flight gets the loading state instead of one frame
  // of a half-populated form.
  const refsState = { ...refs, data: refs.loading ? null : refs.data };

  const customerListRefused = refs.data?.customersRefused === true;
  const namingNewCustomer = customerListRefused || customerId === NEW_CUSTOMER;

  // Amounts are typed by a person, so the pounds-and-pence they type is checked
  // here before the request goes anywhere. The server is still the authority —
  // it parses the same string into minor units — but a disabled button is a
  // kinder answer than a validation panel.
  const cleanedAmount = amount.replace(/[£,\s]/g, '');
  const amountIsValid = /^\d{1,12}(\.\d{1,2})?$/.test(cleanedAmount) && Number(cleanedAmount) > 0;
  const customerIsChosen = namingNewCustomer ? newCustomerName.trim().length > 1 : customerId !== '';

  const missing: string[] = [];
  if (!customerIsChosen) missing.push(namingNewCustomer ? `a name for the ${lower(t.customer)}` : `a ${lower(t.customer)}`);
  if (description.trim().length < 5) missing.push('a description');
  if (!amountIsValid) missing.push('a net amount');
  if (!accountId) missing.push('an income code');
  if (!costCentreId) missing.push(`a ${lower(t.costCentre)}`);

  const create = useMutation(async () =>
    api.post<{ id: string; reference: string; approvalStages: number; firstApproverName: string | null }>(
      '/receivables/invoice-requests',
      {
        customerId: namingNewCustomer ? null : customerId,
        proposedCustomerName: namingNewCustomer ? newCustomerName.trim() : null,
        proposedCustomerEmail: namingNewCustomer && newCustomerEmail.trim() ? newCustomerEmail.trim() : null,
        description: description.trim(),
        amount: cleanedAmount,
        accountId,
        costCentreId,
        vatCode,
      },
    ),
  );

  const reset = () => {
    setCustomerId('');
    setNewCustomerName('');
    setNewCustomerEmail('');
    setDescription('');
    setAmount('');
    setAccountId('');
    setCostCentreId('');
    setVatCode('OUT');
  };

  const close = () => {
    create.clearError();
    onClose();
  };

  return (
    <Modal
      open={open}
      onClose={close}
      title="Request a sales invoice"
      // Wide enough for the two-column blocks below to actually be two columns,
      // so the whole request is on screen at once rather than behind a scroll.
      wide
      footer={
        <>
          <button type="button" className="btn" onClick={close}>
            Cancel
          </button>
          <button
            type="button"
            className="btn btn--primary"
            disabled={create.busy || missing.length > 0}
            onClick={() =>
              void create.run().then((result) => {
                if (!result) return;
                reset();
                onCreated(
                  result.firstApproverName
                    ? `${result.reference} raised. It is now with ${result.firstApproverName} for approval.`
                    : `${result.reference} raised and is awaiting approval.`,
                );
              })
            }
          >
            {create.busy ? 'Submitting…' : 'Submit the request'}
          </button>
        </>
      }
    >
      <AsyncPanel state={refsState}>
        {(data) => (
          <div className="stack">
            {data.customers.length > 0 ? (
              <Select
                label={t.customer}
                value={customerId}
                onChange={(e) => setCustomerId(e.currentTarget.value)}
                required
              >
                <option value="">Choose…</option>
                {data.customers.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.code} — {c.name}
                  </option>
                ))}
                <option value={NEW_CUSTOMER}>Someone else — give their details below</option>
              </Select>
            ) : null}

            {namingNewCustomer ? (
              <div className="grid grid--2">
                <TextInput
                  label={`${t.customer} name`}
                  hint={
                    customerListRefused
                      ? `${t.financeTeam} will match this to an existing ${lower(t.customer)}, or set one up, when the request is converted.`
                      : `A new ${lower(t.customer)} record is created when the request is converted.`
                  }
                  value={newCustomerName}
                  onChange={(e) => setNewCustomerName(e.currentTarget.value)}
                  required
                />
                <TextInput
                  label="Email for the invoice"
                  hint="Optional."
                  type="email"
                  value={newCustomerEmail}
                  onChange={(e) => setNewCustomerEmail(e.currentTarget.value)}
                />
              </div>
            ) : null}

            <TextArea
              label="What is being invoiced?"
              hint={`This appears on the invoice the ${lower(t.customer)} receives. A few words at least.`}
              value={description}
              onChange={(e) => setDescription(e.currentTarget.value)}
              required
            />
            <div className="grid grid--2">
              <TextInput
                label="Net amount"
                hint="Pounds and pence, before VAT — for example 1250.00"
                className="num-input"
                inputMode="decimal"
                value={amount}
                onChange={(e) => setAmount(e.currentTarget.value)}
                required
              />
              <Select label="VAT" value={vatCode} onChange={(e) => setVatCode(e.currentTarget.value)}>
                {data.vatCodes.map((v) => (
                  <option key={v.code} value={v.code}>
                    {v.code} — {v.name}
                  </option>
                ))}
              </Select>
              <Select label="Income code" value={accountId} onChange={(e) => setAccountId(e.currentTarget.value)} required>
                <option value="">Choose…</option>
                {data.accounts.map((a) => (
                  <option key={a.id} value={a.id}>
                    {a.code} — {a.name}
                  </option>
                ))}
              </Select>
              {/* The server requires a cost centre on every request, so the form
                  does too — there is no "none" to choose. */}
              <Select
                label={`${t.costCentre} credited`}
                value={costCentreId}
                onChange={(e) => setCostCentreId(e.currentTarget.value)}
                required
              >
                <option value="">Choose…</option>
                {data.costCentres.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.code} — {c.name}
                  </option>
                ))}
              </Select>
            </div>
            {missing.length > 0 && !create.busy ? <p className="smaller muted">Still needed: {missing.join(', ')}.</p> : null}
            <ErrorNotice error={create.error} />
          </div>
        )}
      </AsyncPanel>
    </Modal>
  );
}
